// dashboard/components/Sidebar.jsx
import { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  School as SchoolIcon,
  LibraryBooks as LibraryBooksIcon,
  Telegram as TelegramIcon,
  Subscriptions as SubscriptionsIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Storefront,
  Security,
  Settings,
  Payment,
  TrendingUp,
  Redeem,
  People,
  Email,
  Help,
  CreditCard,
  Store,
  ChevronLeft as ChevronLeftIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import { BrandIcon, BrandLogo } from '../../branding/BrandAsset';

const Sidebar = ({ active, setActive, collapsed, setCollapsed }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout, user } = useAuth(); // Get user here at the component level
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const navItems = [
    {
      type: "section",
      label: "Main",
      items: [
        { text: "Overview", icon: <DashboardIcon />, id: "Overview" },
        { text: "My Access", icon: <Security />, id: "MyAccess" },
        { text: "Notifications", icon: <NotificationsIcon />, id: "Notifications" },
      ],
    },
    {
      type: "section",
      label: "Academy",
      items: [
        { text: "All Courses", icon: <LibraryBooksIcon />, id: "AllCourses" },
        { text: "My Courses", icon: <SchoolIcon />, id: "MyCourses" },
        { text: "Learning Progress", icon: <TrendingUp />, id: "LearningProgress" },
      ],
    },
    {
      type: "section",
      label: "Telegram Signals",
      items: [
        { text: "Signal Channel", icon: <TelegramIcon />, id: "TelegramSignals" },
        { text: "Subscription Plans", icon: <SubscriptionsIcon />, id: "SubscriptionPlans" },
        { text: "My Subscription", icon: <CreditCard />, id: "MySubscription" },
        { text: "Renew / Extend", icon: <Payment />, id: "RenewSubscription" },
      ],
    },
    {
      type: "section",
      label: "Wallet & Payments",
      items: [
        { text: "My Wallet", icon: <WalletIcon />, id: "Wallet" },
        { text: "Deposit Funds", icon: <Payment />, id: "DepositFunds" },
        { text: "Transaction History", icon: <ReceiptIcon />, id: "Transactions" },
        { text: "Payment History", icon: <ReceiptIcon />, id: "PaymentHistory" },
      ],
    },
    {
      type: "section",
      label: "Marketplace",
      items: [
        { text: "Forex Tools", icon: <Storefront />, id: "ForexTools" },
        { text: "My Tools / Licenses", icon: <Store />, id: "MyTools" },
      ],
    },
    {
      type: "section",
      label: "Rewards",
      items: [
        { text: "Referral Program", icon: <People />, id: "ReferralProgram" },
        { text: "My Referral Earnings", icon: <Redeem />, id: "ReferralEarnings" },
      ],
    },
    {
      type: "section",
      label: "Support",
      items: [
        { text: "Help & Support", icon: <Help />, id: "HelpSupport" },
        { text: "Contact Support", icon: <Email />, id: "ContactSupport" },
      ],
    },
    {
      type: "section",
      label: "Account",
      items: [
        { text: "My Profile", icon: <PersonIcon />, id: "Profile" },
        { text: "Security", icon: <Security />, id: "Security" },
        { text: "Settings", icon: <Settings />, id: "Settings" },
      ],
    },
  ];

  const handleItemClick = (item) => {
    if (item.isLogout) {
      setLogoutDialogOpen(true);
    } else {
      setActive(item.id);
    }
  };

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false);
    await logout();
    navigate('/login');
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  // Helper function to get user initials - now using the user from component scope
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <>
      <Box
        sx={{
          width: collapsed ? 80 : 260,
          height: "100vh",
          backgroundColor: "#001f3f",
          p: collapsed ? 1 : 2,
          transition: "width 0.3s ease",
          borderRight: "1px solid rgba(255,255,255,0.05)",
          position: 'fixed',
          zIndex: 100,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.down('sm')]: {
            width: collapsed ? 0 : 260,
            p: collapsed ? 0 : 2,
            overflow: 'hidden',
          }
        }}
      >
        {/* Logo / Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: collapsed ? "center" : "space-between",
            alignItems: "center",
            mb: 2,
            pl: collapsed ? 0 : 1,
            pr: collapsed ? 0 : 1,
            minHeight: 48,
          }}
        >
          {collapsed ? (
            <BrandIcon surface="dark" alt="ProTradesInsight" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          ) : (
            <BrandLogo surface="dark" alt="ProTradesInsight" style={{ width: 155, maxHeight: 48, objectFit: 'contain' }} />
          )}

          <IconButton 
            onClick={() => setCollapsed((prev) => !prev)}
            sx={{
              marginLeft: collapsed ? 0 : 'auto',
              marginRight: collapsed ? 0 : '8px',
              color: '#ffffff',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        {/* User Profile */}
        {!collapsed && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              mx: 0.5,
              mt: 1,
              mb: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.05)',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#2ecc71',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: '14px',
              }}
            >
              {getUserInitials()}
            </Box>
            <Box sx={{ ml: 1.5, flex: 1, overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {user?.first_name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>
                {user?.email || ''}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '10px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }}>
          {navItems.map((section, sectionIndex) => (
            <Box key={sectionIndex}>
              {!collapsed && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 1,
                    pt: 2,
                    pb: 0.5,
                    color: 'rgba(255,255,255,0.4)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    fontSize: '0.6rem',
                  }}
                >
                  {section.label}
                </Typography>
              )}
              <List sx={{ 
                px: collapsed ? 0 : 1,
                '& .MuiListItemText-primary': {
                  fontSize: '0.9rem !important',
                }
              }}>
                {section.items.map((item) => {
                  const isActive = active === item.id;

                  return (
                    <Tooltip title={collapsed ? item.text : ""} placement="right" key={item.text}>
                      <ListItem
                        button
                        onClick={() => handleItemClick(item)}
                        sx={{
                          mb: 0.5,
                          borderRadius: "10px",
                          px: collapsed ? 1.5 : 2,
                          py: 0.8,
                          backgroundColor: isActive ? "rgba(46, 204, 113, 0.15)" : "transparent",
                          borderLeft: isActive ? "3px solid #2ecc71" : "3px solid transparent",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: collapsed ? 0 : 36,
                            color: isActive ? "#2ecc71" : "rgba(255,255,255,0.6)",
                            mr: collapsed ? 0 : 1.5,
                            justifyContent: 'center',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {!collapsed && (
                          <ListItemText
                            primary={
                              <Typography 
                                sx={{
                                  color: isActive ? "#ffffff" : "rgba(255,255,255,0.7)",
                                  fontSize: '0.85rem',
                                  fontWeight: isActive ? 600 : 400
                                }}
                              >
                                {item.text}
                              </Typography>
                            }
                          />
                        )}
                      </ListItem>
                    </Tooltip>
                  );
                })}
              </List>
              {sectionIndex < navItems.length - 1 && !collapsed && (
                <Divider sx={{ mx: 2, my: 0.5, backgroundColor: 'rgba(255,255,255,0.05)' }} />
              )}
            </Box>
          ))}
        </Box>

        {/* Logout Button */}
        <Box sx={{ 
          pt: 1, 
          borderTop: '1px solid rgba(255,255,255,0.05)',
          mt: 'auto',
        }}>
          <Tooltip title={collapsed ? "Logout" : ""} placement="right">
            <ListItem
              button
              onClick={() => setLogoutDialogOpen(true)}
              sx={{
                borderRadius: "10px",
                px: collapsed ? 1.5 : 2,
                py: 0.8,
                backgroundColor: "rgba(231, 76, 60, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(231, 76, 60, 0.2)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  color: "#e74c3c",
                  mr: collapsed ? 0 : 1.5,
                  justifyContent: 'center',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={
                    <Typography sx={{ color: "#e74c3c", fontSize: '0.85rem', fontWeight: 500 }}>
                      Logout
                    </Typography>
                  }
                />
              )}
            </ListItem>
          </Tooltip>
        </Box>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title" sx={{ color: '#d32f2f' }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to logout? You will be redirected to the login page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained" autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;
